import React from 'react';
import Accordion from 'react-bootstrap/Accordion';
import './AITrailViewer.css';
import {Badge} from "react-bootstrap";

const AITrailViewer = ({ ai_trail }) => {
    if (!ai_trail || !ai_trail.categories || ai_trail.categories.length === 0) {
        return <div className="ai_trail-empty">No AI data trail available.</div>;
    }
    const maxScoreIndex = ai_trail?.categories?.reduce((maxIndex, current, currentIndex, array) => Number(current.score) > Number(array[maxIndex].score) ? currentIndex : maxIndex, 0);
    // Helper to chunk arrays into groups of a specific size (e.g., 4)
    const chunkArray = (array, size) => {
        if (!array) return [];
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
            chunks.push(array.slice(i, i + size));
        }
        return chunks;
    };

    const fDesc = (desc) => {
        if (!desc) return "";
        const parts = desc.split(/(MEGABOOST)/gi);
        return (
            <React.Fragment>
                {parts.map((part, index) =>
                    part.toUpperCase() === 'MEGABOOST' ? (
                        <strong key={index}>{part}</strong>
                    ) : (
                        part
                    )
                )}
            </React.Fragment>
        );
    }

    return (
        <div className="ai_trail-container">
            {ai_trail?.priorities && (
                <React.Fragment>
                    <div className={"text-muted mb-1"}><i>Προτεραιότητες Κατηγοριών</i></div>
                    <div className="ai_trail_mini_boxes-container mb-3">
                        {ai_trail?.priorities.map((item, index) => (
                            <div key={index} className="ai_trail_mini_boxes-card">
                                <div className="ai_trail_mini_boxes-header">
                                    <h3>{item.categoryName}</h3>
                                    <span className={`ai_trail_mini_boxes-badge ${item.priority > 0 ? 'active' : 'empty'}`}>{item.priority.toFixed(2)}</span>
                                </div>

                                <div className="ai_trail_mini_boxes-body">
                                    {item.scoreAnalysis.length === 0 ? (
                                        <p className="ai_trail_mini_boxes-no-data">No active analyses available.</p>
                                    ) : (
                                        <ul className="ai_trail_mini_boxes-list">
                                            {item.scoreAnalysis.map((analysis, idx) => (
                                                <li key={idx} className="ai_trail_mini_boxes-list-item">
                                                    <span className="ai_trail_mini_boxes-desc">
                                                        <Badge bg={"primary"} style={{padding: "4px"}} className={"mr-1"}>{analysis.from}</Badge>
                                                        {fDesc(analysis.description)}
                                                    </span>
                                                    <strong className="ai_trail_mini_boxes-amount">{analysis.amount.toFixed(2)}</strong>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </React.Fragment>
            )}
            <div className={"text-muted mb-1"}><i>Scores Κατηγοριών</i></div>
            <Accordion defaultActiveKey={String(maxScoreIndex)} className="ai_trail-accordion-group">
                {ai_trail.categories.map((categoryData, catIndex) => {
                    const { name: categoryName, score: categoryScore, entities = [], races = [] } = categoryData;

                    const entityRows = chunkArray(entities, 3);
                    const raceRows = chunkArray(races, 3);
                    const eventKey = String(catIndex);

                    return (
                        <Accordion.Item eventKey={eventKey} key={eventKey} className="ai_trail-accordion-item">
                            <Accordion.Header>
                                <div className="ai_trail-header-content">
                                    <span className="ai_trail-badge">{categoryName}</span>
                                    <span className="ai_trail-total-score">
                                        Score: <strong className="ai_trail-score-highlight">{categoryScore?.toFixed(2)}%</strong>
                                    </span>
                                </div>
                            </Accordion.Header>

                            <Accordion.Body className="ai_trail-accordion-body">
                                {/* BIG ROW 1: ENTITIES */}
                                <div className="ai_trail-section">
                                    <h3 className="ai_trail-section-title">
                                        Matched Entities <span className="ai_trail-count">({entities.length})</span>
                                    </h3>
                                    {entityRows.length === 0 ? (
                                        <p className="ai_trail-no-data">No entities.</p>
                                    ) : (
                                        entityRows.map((row, rowIndex) => (
                                            <div key={`entity-row-${rowIndex}`} className="ai_trail-grid-row">
                                                {row.map((entity, index) => (
                                                    <div key={index} className="ai_trail-box ai_trail-box-entity">
                                                        <div className="ai_trail-box-header">
                                                            <span className="ai_trail-type-tag">{entity.entityType}</span>
                                                            <span className="ai_trail-box-score">{entity.score?.toFixed(2)}%</span>
                                                        </div>
                                                        <h4 className="ai_trail-entity-name" title={entity.entityName}>
                                                            {entity.entityName}
                                                        </h4>
                                                        <div className="ai_trail-analysis-list">
                                                            <h5>Score Analysis:</h5>
                                                            {entity.scoreAnalysis?.map((analysis, aIdx) => (
                                                                <div key={aIdx} className="ai_trail-analysis-item">
                                                                    • {analysis.description}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* BIG ROW 2: RACES */}
                                <div className="ai_trail-section">
                                    <h3 className="ai_trail-section-title">
                                        Race Scores <span className="ai_trail-count">({races.length})</span>
                                    </h3>
                                    {raceRows.length === 0 ? (
                                        <p className="ai_trail-no-data">No races.</p>
                                    ) : (
                                        raceRows.map((row, rowIndex) => (
                                            <div key={`race-row-${rowIndex}`} className="ai_trail-grid-row">
                                                {row.map((race, index) => (
                                                    <div key={index} className="ai_trail-box ai_trail-box-race">
                                                        <div className="ai_trail-box-header">
                                                            <span className="ai_trail-type-tag ai_trail-type-race">{race.entityType}</span>
                                                            <span className="ai_trail-box-score ai_trail-score-accent">{race.score?.toFixed(2)}%</span>
                                                        </div>
                                                        <h4 className="ai_trail-entity-name" title={race.entityName}>
                                                            {race.entityName}
                                                        </h4>
                                                        <div className="ai_trail-analysis-list">
                                                            <h5>SCORING Breakdown:</h5>
                                                            {race.scoreAnalysis?.map((analysis, aIdx) => (
                                                                <div key={aIdx} className="ai_trail-analysis-item">
                                                                    • {analysis.description}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    );
                })}
            </Accordion>
        </div>
    );
}

export default AITrailViewer;
